import React from "react";
import { Link } from "react-router-dom";
import { Sparkles, Loader2 } from "lucide-react";

const EQUIPMENT_OPTIONS = [
  "bodyweight", "dumbbells", "barbell", "machines",
  "kettlebell", "resistance bands", "pull-up bar",
];

/**
 * AI Workout Generator form — pure controlled inputs. Lives in its own file
 * to keep WorkoutPage.jsx focused on the post-generation plan view.
 *
 * All state is owned by the parent so the values can persist while the user
 * regenerates or navigates away.
 */
export default function WorkoutGenerateForm({
  // form fields
  goal, setGoal,
  level, setLevel,
  days, setDays,
  weeks, setWeeks,
  sessionDuration, setSessionDuration,
  equipment, toggleEquip,
  notes, setNotes,
  // submission
  generating,
  onGenerate,
}) {
  return (
    <div className="tracing-border p-8 mb-8" data-testid="workout-form">
      <div className="label-eyebrow text-[#6B9BFF] mb-4 flex items-center gap-2">
        <Sparkles className="w-4 h-4" /> AI Workout Generator
      </div>
      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <label className="label-eyebrow text-white/50 block mb-2">Goal</label>
          <select
            value={goal}
            onChange={(e) => setGoal(e.target.value)}
            className="w-full bg-[#1A1A1A] border border-white/10 px-4 py-3 focus:outline-none focus:border-[#6B9BFF]"
            data-testid="workout-goal-select"
          >
            <option value="lose_fat">Lose fat</option>
            <option value="build_muscle">Build muscle</option>
            <option value="maintain">Maintain</option>
            <option value="endurance">Endurance</option>
          </select>
        </div>
        <div>
          <label className="label-eyebrow text-white/50 block mb-2">Fitness level</label>
          <select
            value={level}
            onChange={(e) => setLevel(e.target.value)}
            className="w-full bg-[#1A1A1A] border border-white/10 px-4 py-3 focus:outline-none focus:border-[#6B9BFF]"
            data-testid="workout-level-select"
          >
            <option value="beginner">Beginner</option>
            <option value="intermediate">Intermediate</option>
            <option value="advanced">Advanced</option>
          </select>
        </div>
        <div>
          <label className="label-eyebrow text-white/50 block mb-2">Days per week</label>
          <input
            type="number"
            min={1}
            max={7}
            value={days}
            onChange={(e) => setDays(e.target.value)}
            className="w-full bg-[#1A1A1A] border border-white/10 px-4 py-3 focus:outline-none focus:border-[#6B9BFF]"
            data-testid="workout-days-input"
          />
        </div>
        <div>
          <label className="label-eyebrow text-white/50 block mb-2">Program length (weeks)</label>
          <input
            type="number"
            min={1}
            max={12}
            value={weeks}
            onChange={(e) => setWeeks(e.target.value)}
            className="w-full bg-[#1A1A1A] border border-white/10 px-4 py-3 focus:outline-none focus:border-[#6B9BFF]"
            data-testid="workout-weeks-input"
          />
          <div className="text-[10px] text-white/40 mt-1">1–12 weeks. Last week is auto-deload for 4+ week programs.</div>
        </div>
        <div>
          <label className="label-eyebrow text-white/50 block mb-2">Session length</label>
          <select
            value={sessionDuration}
            onChange={(e) => setSessionDuration(e.target.value)}
            className="w-full bg-[#1A1A1A] border border-white/10 px-4 py-3 focus:outline-none focus:border-[#6B9BFF]"
            data-testid="workout-duration-select"
          >
            {[15, 30, 45, 60, 75, 90, 105, 120].map((m) => (
              <option key={m} value={m}>
                {m} minutes
              </option>
            ))}
          </select>
          <div className="text-[10px] text-white/40 mt-1">Each training day will target this length.</div>
        </div>
        <div>
          <label className="label-eyebrow text-white/50 block mb-2">Equipment</label>
          <div className="flex flex-wrap gap-2">
            {EQUIPMENT_OPTIONS.map((eq) => (
              <button
                key={eq}
                type="button"
                onClick={() => toggleEquip(eq)}
                className={`px-3 py-2 text-xs uppercase tracking-wider border transition-colors ${
                  equipment.includes(eq)
                    ? "border-[#6B9BFF] text-[#6B9BFF] bg-[#6B9BFF]/5"
                    : "border-white/10 text-white/60 hover:border-white/30"
                }`}
                data-testid={`equip-${eq.replace(/\s/g, "-")}`}
              >
                {eq}
              </button>
            ))}
          </div>
        </div>
        <div className="md:col-span-2">
          <label className="label-eyebrow text-white/50 block mb-2">Notes (optional)</label>
          <textarea
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Knee injury, prefer morning sessions, focus on glutes…"
            className="w-full bg-[#1A1A1A] border border-white/10 px-4 py-3 focus:outline-none focus:border-[#6B9BFF]"
            data-testid="workout-notes-input"
          />
        </div>
      </div>
      <button
        disabled={generating}
        onClick={onGenerate}
        className="mt-6 bg-gradient-to-r from-[#7C3AED] to-[#6B9BFF] text-white font-black uppercase tracking-widest px-8 py-4 inline-flex items-center gap-2 disabled:opacity-50"
        data-testid="generate-workout-button"
      >
        {generating ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" /> Generating…
          </>
        ) : (
          <>
            <Sparkles className="w-4 h-4" /> Generate my plan
          </>
        )}
      </button>
      <p className="text-xs text-white/40 mt-3">
        Make sure your <Link to="/app/profile" className="text-[#6B9BFF] underline">profile</Link> has your weight, age and height for best results.
      </p>
    </div>
  );
}

export { EQUIPMENT_OPTIONS };
